"use client";

import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  getPreviewAnalyticsRecords,
  deleteAnalyticsRecord,
  deleteAnalyticsRecords,
  deleteAllPreviewAnalytics,
} from "@/services/analytics-dashboard-service";
import { Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { subDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { AnalyticsStats } from "./analytics-stats";
import { AnalyticsFilters } from "./analytics-filters";
import { AnalyticsTableView } from "./analytics-table-view";
import { AnalyticsPagination } from "./analytics-pagination";

// Add the missing type definitions
interface AnalyticsTableProps {
  previewId: string;
  className?: string;
}

interface AnalyticsRecord {
  id: string;
  first_seen_at: string;
  last_seen_at: string;
  total_duration_seconds: number;
  country_code?: string;
  city?: string;
  region?: string;
  device_type?: string;
  browser_name?: string;
  operating_system?: string;
  ip_address?: string;
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
};

const formatTimeAgo = (dateString: string) => {
  const now = new Date();
  const past = new Date(dateString);
  const diffInMinutes = Math.floor(
    (now.getTime() - past.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) return "just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return `${Math.floor(diffInMinutes / 1440)}d ago`;
};

export default function AnalyticsTable({
  previewId,
  className,
}: AnalyticsTableProps) {
  const { t } = useTranslation("analytics");
  const [allRecords, setAllRecords] = useState<AnalyticsRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filtering state
  const [startDate, setStartDate] = useState<Date | undefined>(
    subDays(new Date(), 30)
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [searchQuery, setSearchQuery] = useState("");

  // Selection state for bulk delete
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(
    new Set()
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPaginatedRecords = useCallback(
    async (page: number, size: number, isRefresh = false) => {
      try {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoadingRecords(true);
        }

        // Use the new dashboard service
        const result = await getPreviewAnalyticsRecords(previewId, {
          page,
          pageSize: size,
          startDate,
          endDate,
          searchQuery: searchQuery.trim(),
        });

        setTotalRecords(result.totalCount);
        setAllRecords(result.data);
      } catch (err) {
        console.error("Error fetching records:", err);
        toast.error(t("analytics:analyticsTable.toast.error"));
      } finally {
        setLoadingRecords(false);
        setRefreshing(false);
      }
    },
    [previewId, startDate, endDate, searchQuery]
  );

  // Single useEffect to handle all data fetching
  useEffect(() => {
    if (previewId) {
      fetchPaginatedRecords(currentPage, pageSize);
    }
  }, [
    previewId,
    currentPage,
    pageSize,
    startDate,
    endDate,
    searchQuery,
    fetchPaginatedRecords,
  ]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [startDate, endDate, searchQuery]);

  const handleRefresh = () => {
    fetchPaginatedRecords(currentPage, pageSize, true);
  };

  const clearFilters = () => {
    setStartDate(subDays(new Date(), 30));
    setEndDate(new Date());
    setSearchQuery("");
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || startDate || endDate);

  const totalPages = Math.ceil(totalRecords / pageSize);
  const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  const handleDeleteRecord = async (recordId: string) => {
    setIsDeleting(true);
    try {
      const success = await deleteAnalyticsRecord(recordId);
      if (success) {
        toast.success(t("analytics:analyticsTable.toast.successDelete"));
        fetchPaginatedRecords(currentPage, pageSize);
      } else {
        toast.error(t("analytics:analyticsTable.toast.error"));
      }
    } catch (error) {
      toast.error(t("analytics:analyticsTable.toast.error"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRecords.size === 0) return;

    setIsDeleting(true);
    try {
      const success = await deleteAnalyticsRecords(Array.from(selectedRecords));
      if (success) {
        toast.success(
          t("analytics:analyticsTable.toast.successDeleteSelected", {
            selectedRecordsSize: selectedRecords.size,
          })
        );
        setSelectedRecords(new Set());
        fetchPaginatedRecords(currentPage, pageSize);
      } else {
        toast.error(t("analytics:analyticsTable.toast.errorDeleteSelected"));
      }
    } catch (error) {
      toast.error(t("analytics:analyticsTable.toast.errorDeleteSelected"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const success = await deleteAllPreviewAnalytics(previewId);
      if (success) {
        toast.success(t("analytics:analyticsTable.toast.allSessionsDeleted"));
        setSelectedRecords(new Set());
        fetchPaginatedRecords(currentPage, pageSize);
      } else {
        toast.error(t("analytics:analyticsTable.toast.errorDeleteAll"));
      }
    } catch (error) {
      toast.error(t("analytics:analyticsTable.toast.errorDeleteAll"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRecords(new Set(allRecords.map((record) => record.id)));
    } else {
      setSelectedRecords(new Set());
    }
  };

  const handleSelectRecord = (recordId: string, checked: boolean) => {
    const newSelected = new Set(selectedRecords);
    if (checked) {
      newSelected.add(recordId);
    } else {
      newSelected.delete(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t("analytics:analyticsTable.title")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("analytics:analyticsTable.description")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw
                  className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
                />
                {t("analytics:analyticsTable.refresh")}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Stats Overview */}
          <AnalyticsStats
            totalRecords={totalRecords}
            allRecords={allRecords}
            formatDuration={formatDuration}
          />

          {/* Compact Filters and Actions Bar */}
          <AnalyticsFilters
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            selectedRecords={selectedRecords}
            isDeleting={isDeleting}
            hasActiveFilters={hasActiveFilters}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearchQueryChange={setSearchQuery}
            onClearFilters={clearFilters}
            onDeleteSelected={handleDeleteSelected}
            onDeleteAll={handleDeleteAll}
          />

          {/* Table */}
          <AnalyticsTableView
            allRecords={allRecords}
            selectedRecords={selectedRecords}
            isDeleting={isDeleting}
            loadingRecords={loadingRecords}
            pageSize={pageSize}
            hasActiveFilters={hasActiveFilters}
            formatDuration={formatDuration}
            formatTimeAgo={formatTimeAgo}
            onSelectAll={handleSelectAll}
            onSelectRecord={handleSelectRecord}
            onDeleteRecord={handleDeleteRecord}
            onClearFilters={clearFilters}
          />

          {/* Table Controls and Pagination */}
          <AnalyticsPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            pageSize={pageSize}
            startRecord={startRecord}
            endRecord={endRecord}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
